import { assign, fromPromise, setup } from "xstate";

// Guest-data lifecycle: REGISTER auto-claims (no prompt), LOGIN prompts.
// See docs/ARCHITECTURE_MULTIACCOUNT.md (guest always ends empty, no duplication).
function isError(value: unknown): value is Error {
  return value instanceof Error;
}

export type AuthFlowEvent =
  | { type: "SUBMIT"; email: string; password: string }
  | { type: "SUBMIT_SIGNUP"; name: string; email: string; password: string }
  | { type: "SUBMIT_MFA"; otp: string }
  | { type: "CONFIRM_MERGE" }
  | { type: "CONFIRM_DISCARD" }
  | { type: "CANCEL" }
  | { type: "RETRY" };

/**
 * Result of the sign-in actor. A 2FA-enabled account does NOT complete the
 * login on the first call: the server replies `mfa_required: true` with a
 * short-lived challenge token (`mfa_token`) and NO session. The actor surfaces
 * that so the machine can branch into the MFA challenge step instead of
 * treating the (session-less) response as a successful login.
 */
export interface SignInActorResult {
  mfaRequired: boolean;
  /** Challenge token to replay to `verify-mfa` (login `mfa_token`). */
  mfaToken: string | null;
}

export interface AuthFlowContext {
  error: string | null;
  name: string;
  email: string;
  password: string;
  mode: "signIn" | "signUp";
  /** Pending 2FA challenge token (login `mfa_token`), or `null` when idle. */
  mfaToken: string | null;
  /** The OTP currently being verified (carried into `verifyMfaActor`). */
  otp: string;
}

export const authFlowMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: AuthFlowContext;
    events: AuthFlowEvent;
  },
  actors: {
    checkUnsyncedActor: fromPromise(async () => {
      await Promise.resolve();
      return false;
    }),
    clearDataActor: fromPromise(async () => {
      await Promise.resolve();
    }),
    signInActor: fromPromise(
      async (_opts: { input: { email: string; password: string } }): Promise<SignInActorResult> => {
        await Promise.resolve();
        return { mfaRequired: false, mfaToken: null };
      },
    ),
    verifyMfaActor: fromPromise(
      async (_opts: { input: { requestToken: string; otp: string } }): Promise<void> => {
        await Promise.resolve();
      },
    ),
    signUpActor: fromPromise(
      async (_opts: {
        input: { name: string; email: string; password: string };
      }): Promise<void> => {
        await Promise.resolve();
      },
    ),
  },
}).createMachine({
  id: "authFlow",
  initial: "idle",
  context: {
    error: null,
    name: "",
    email: "",
    password: "",
    mode: "signIn",
    mfaToken: null,
    otp: "",
  },
  states: {
    idle: {
      on: {
        SUBMIT: {
          target: "checkingUnsynced",
          actions: assign({
            email: ({ event }) => event.email,
            password: ({ event }) => event.password,
            mode: "signIn",
            error: null,
          }),
        },
        SUBMIT_SIGNUP: {
          target: "checkingUnsynced",
          actions: assign({
            name: ({ event }) => event.name,
            email: ({ event }) => event.email,
            password: ({ event }) => event.password,
            mode: "signUp",
            error: null,
          }),
        },
      },
    },

    checkingUnsynced: {
      invoke: {
        src: "checkUnsyncedActor",
        onDone: [
          // Guest HAS unsynced data (REGISTER or LOGIN) → PROMPT keep-vs-discard.
          // CC-28: the old "auto-claim on register" shortcut (signUp skipped
          // `confirmingMerge`) was removed — it made the Discard path unreachable
          // and broke the guest-migration contract. A registering user with
          // local guest data must be able to choose merge OR discard, exactly
          // like login.
          {
            guard: ({ event }) => event.output,
            target: "confirmingMerge",
          },
          { target: "authenticating" },
        ],
        onError: {
          target: "authenticating", // Fail-safe
        },
      },
    },

    confirmingMerge: {
      on: {
        CONFIRM_MERGE: "authenticating",
        CONFIRM_DISCARD: "clearingLocalData",
        CANCEL: "idle",
      },
    },

    clearingLocalData: {
      invoke: {
        src: "clearDataActor",
        onDone: "authenticating",
        onError: {
          target: "idle",
          actions: assign({ error: "Failed to clear local data" }),
        },
      },
    },

    authenticating: {
      always: [
        { target: "signingIn", guard: ({ context }) => context.mode === "signIn" },
        { target: "signingUp", guard: ({ context }) => context.mode === "signUp" },
      ],
    },

    signingIn: {
      invoke: {
        src: "signInActor",
        input: ({ context }) => ({
          email: context.email,
          password: context.password,
        }),
        onDone: [
          // 2FA-enabled account: the server issued a challenge instead of a
          // session. Stash the challenge token and ask for the OTP — do NOT
          // treat this as a completed login.
          {
            guard: ({ event }) => event.output.mfaRequired,
            target: "mfaChallenge",
            actions: assign({
              mfaToken: ({ event }) => event.output.mfaToken,
              error: null,
            }),
          },
          { target: "success" },
        ],
        onError: {
          target: "idle",
          actions: assign({
            error: ({ event }) => {
              const err = event.error;
              if (isError(err) && err.message) {
                return err.message;
              }
              return "Login failed";
            },
          }),
        },
      },
    },

    // Resting step: email+password verified, waiting for the 6-digit TOTP code.
    mfaChallenge: {
      on: {
        SUBMIT_MFA: {
          target: "verifyingMfa",
          actions: assign({
            otp: ({ event }) => event.otp,
            error: null,
          }),
        },
        // Back out of the challenge: forget the token and return to the form.
        CANCEL: {
          target: "idle",
          actions: assign({ mfaToken: null, otp: "", error: null }),
        },
      },
    },

    verifyingMfa: {
      invoke: {
        src: "verifyMfaActor",
        input: ({ context }) => ({
          requestToken: context.mfaToken ?? "",
          otp: context.otp,
        }),
        onDone: "success",
        // Wrong/expired code: return to the challenge so the user can retry,
        // keeping the (still valid) challenge token.
        onError: {
          target: "mfaChallenge",
          actions: assign({
            otp: "",
            error: ({ event }) => {
              const err = event.error;
              if (isError(err) && err.message) {
                return err.message;
              }
              return "Invalid code";
            },
          }),
        },
      },
    },

    signingUp: {
      invoke: {
        src: "signUpActor",
        input: ({ context }) => ({
          name: context.name,
          email: context.email,
          password: context.password,
        }),
        onDone: "success",
        onError: {
          target: "idle",
          actions: assign({
            error: ({ event }) => {
              const err = event.error;
              if (isError(err) && err.message) {
                return err.message;
              }
              return "Registration failed";
            },
          }),
        },
      },
    },

    success: {
      type: "final",
    },
  },
});
